import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { ApiService } from '@/_services/api.service';
import { useStreamingMessage } from '../streaming/useStreamingMessage';
import { useMessagesCache } from '../queries/useMessagesStore';
import { clearInterruptAtom } from '../atoms/interruptAtoms';
import { queryKeys } from '../queries/queryKeys';
import type { IMessage } from '@/types';

interface EditMessageParams {
	conversationId: string;
	messageId: string;
	text: string;
}

/**
 * Mutation hook to edit a previous user message and get a new AI response.
 *
 * Flow:
 * 1. Optimistically truncate cache to messages before the edited one + updated message
 * 2. Start SSE stream that returns the edited message + new AI response
 * 3. stream_end commits new AI message to cache
 */
export const useEditMessage = () => {
	const { startMessageStream } = useStreamingMessage();
	const { getMessages, setMessages } = useMessagesCache();
	const clearInterrupt = useSetAtom(clearInterruptAtom);
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (params: EditMessageParams) => params,
		onSuccess: ({ conversationId, messageId, text }) => {
			clearInterrupt();
			const currentMessages = getMessages(conversationId);
			const editIndex = currentMessages.findIndex((m) => m.id === messageId);

			if (editIndex === -1) return;

			const isFirstMessage = editIndex === 0;

			// Optimistic update: keep messages before edit point + updated user message
			const truncated = currentMessages.slice(0, editIndex);
			const updatedMessage: IMessage = {
				...currentMessages[editIndex],
				text,
				updatedAt: new Date()
			};
			setMessages(conversationId, [...truncated, updatedMessage]);

			// Start streaming - SSE will send stream_start, AI response, then stream_end
			startMessageStream(async (signal) => ApiService.EditMessage({ conversationId, messageId, text, signal }), {
				conversationId,
				skipMessageEvents: true,
				skipStreamStart: true,
				onComplete: isFirstMessage
					? () => queryClient.invalidateQueries({ queryKey: queryKeys.conversations.root })
					: undefined
			});
		}
	});
};
